import { useCallback, useEffect, useMemo, useState } from 'react';

import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { isAdministrateur } from '../../../utils/adminAccess';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import { AcademicPanelCard, AcademicPanelListItem, AcademicPanelTableWrap } from './AcademicPanelCard';
import ElearningFileUpload from '../../elearning/ElearningFileUpload';
import ElearningExerciseEditor, {
  exerciseQuestionsToJson,
  type ExerciseQuestion,
} from '../../elearning/ElearningExerciseEditor';
import {
  createAdminElearningCours,
  createAdminElearningForum,
  createAdminElearningRessource,
  deleteAdminElearningCours,
  deleteAdminElearningForum,
  deleteAdminElearningRessource,
  fetchAdminElearningCompletionStats,
  fetchAdminElearningCours,
  fetchAdminElearningDashboard,
  fetchAdminElearningForumMessages,
  fetchAdminElearningForums,
  fetchAdminElearningRessources,
  fetchFilieres,
  moderateAdminElearningForumMessage,
  patchAdminElearningCours,
  patchAdminElearningForum,
  patchAdminElearningRessource,
} from '../../../services/academiaAdminService';
import { BIBLIOTHEQUE_CATEGORIES } from '../../../utils/elearningBibliothequeCategories';
import { BIBLIOTHEQUE_LANGUES } from '../../../utils/elearningBibliothequeLanguages';

type TabId = 'vue' | 'stats' | 'cours' | 'ressources' | 'bibliotheque' | 'forums' | 'moderation';

const RESSOURCE_TYPES = [
  { value: 'VIDEO', label: 'Vidéo pédagogique' },
  { value: 'SUPPORT', label: 'Support numérique' },
  { value: 'EXERCICE', label: 'Exercice interactif' },
  { value: 'BIBLIOTHEQUE', label: 'Bibliothèque numérique' },
] as const;

const RESSOURCE_TYPE_LABELS: Record<string, string> = {
  VIDEO: 'Vidéo',
  SUPPORT: 'Support',
  EXERCICE: 'Exercice',
  BIBLIOTHEQUE: 'Bibliothèque',
};

const FORUM_MESSAGE_STATUT_LABELS: Record<string, string> = {
  VISIBLE: 'Visible',
  MASQUE: 'Masqué',
  SUPPRIME: 'Supprimé',
};

function labelRessourceType(type: unknown) {
  return RESSOURCE_TYPE_LABELS[String(type)] ?? String(type);
}

const FICHE_ITEMS = [
  { key: 'coursEnLigne', label: 'Cours en ligne' },
  { key: 'videosPedagogiques', label: 'Vidéos pédagogiques' },
  { key: 'supportsNumeriques', label: 'Supports numériques' },
  { key: 'exercicesInteractifs', label: 'Exercices interactifs' },
  { key: 'bibliothequeNumerique', label: 'Bibliothèque numérique' },
  { key: 'forumsPedagogiques', label: 'Forums pédagogiques' },
] as const;

const fieldClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

type ToastState = { message: string; variant: 'success' | 'error' } | null;

export default function AcademiaElearningPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const [tab, setTab] = useState<TabId>('vue');
  const [dashboard, setDashboard] = useState<Record<string, unknown> | null>(null);
  const [filieres, setFilieres] = useState<Array<{ id: string; libelle: string; code: string }>>([]);
  const [idFiliere, setIdFiliere] = useState('');
  const [cours, setCours] = useState<Array<Record<string, unknown>>>([]);
  const [ressources, setRessources] = useState<Array<Record<string, unknown>>>([]);
  const [forums, setForums] = useState<Array<Record<string, unknown>>>([]);
  const [stats, setStats] = useState<Record<string, unknown> | null>(null);
  const [selectedForumId, setSelectedForumId] = useState('');
  const [forumMessages, setForumMessages] = useState<Array<Record<string, unknown>>>([]);
  const [moderationMotif, setModerationMotif] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const [coursForm, setCoursForm] = useState({ titre: '', description: '', estPublie: false });
  const [ressourceForm, setRessourceForm] = useState({
    type: 'VIDEO',
    idCours: '',
    titre: '',
    description: '',
    urlExterne: '',
    urlFichier: '',
    storageKey: '',
    estPublie: false,
    auteurBibliotheque: '',
    categorieBibliotheque: '',
    tagsBibliotheque: '',
    langueBibliotheque: '',
    anneePublication: '',
  });
  const [biblioFilters, setBiblioFilters] = useState({ q: '', categorie: '', tag: '' });
  const [exerciseQuestions, setExerciseQuestions] = useState<ExerciseQuestion[]>([]);
  const [forumForm, setForumForm] = useState({ titre: '', description: '', estOuvert: true });

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, f] = await Promise.all([fetchAdminElearningDashboard(), fetchFilieres()]);
      setDashboard(dash);
      setFilieres(f ?? []);
      if (!idFiliere && f?.[0]?.id) setIdFiliere(f[0].id);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [idFiliere, showToast]);

  const loadTabData = useCallback(async () => {
    if (!idFiliere) return;
    try {
      if (tab === 'cours' || tab === 'ressources') {
        const [c, r] = await Promise.all([
          fetchAdminElearningCours(idFiliere),
          fetchAdminElearningRessources({ idFiliere }),
        ]);
        setCours(c);
        setRessources(r);
      } else if (tab === 'bibliotheque') {
        setRessources(
          await fetchAdminElearningRessources({
            idFiliere,
            type: 'BIBLIOTHEQUE',
            q: biblioFilters.q || undefined,
            categorie: biblioFilters.categorie || undefined,
            tag: biblioFilters.tag || undefined,
          }),
        );
      } else if (tab === 'forums' || tab === 'moderation') {
        setForums(await fetchAdminElearningForums(idFiliere));
      } else if (tab === 'stats') {
        setStats(await fetchAdminElearningCompletionStats(idFiliere));
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Erreur', 'error');
    }
  }, [biblioFilters.categorie, biblioFilters.q, biblioFilters.tag, idFiliere, showToast, tab]);

  useEffect(() => {
    void loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (tab !== 'vue') void loadTabData();
  }, [loadTabData, tab, idFiliere]);

  useRegisterPortalRefresh(useCallback(() => {
    void loadBase();
    void loadTabData();
  }, [loadBase, loadTabData]));

  const coursOptions = useMemo(
    () => [{ value: '', label: '— Hors cours (bibliothèque seule) —' }, ...cours.map((c) => ({ value: String(c.id), label: String(c.titre) }))],
    [cours],
  );

  const couverture = (dashboard?.couvertureFiche ?? {}) as Record<string, boolean>;

  async function handleCreateCours(e: React.FormEvent) {
    e.preventDefault();
    if (!idFiliere || !coursForm.titre.trim()) return;
    setBusy(true);
    try {
      await createAdminElearningCours({ idFiliere, ...coursForm });
      setCoursForm({ titre: '', description: '', estPublie: false });
      showToast('Cours créé', 'success');
      await loadTabData();
      await loadBase();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateRessource(e: React.FormEvent) {
    e.preventDefault();
    if (!idFiliere || !ressourceForm.titre.trim()) return;
    if (tab === 'bibliotheque' && !ressourceForm.categorieBibliotheque) {
      showToast('Veuillez choisir une catégorie', 'error');
      return;
    }
    if (tab === 'bibliotheque' && !ressourceForm.langueBibliotheque) {
      showToast('Veuillez choisir une langue', 'error');
      return;
    }
    setBusy(true);
    try {
      let contenuJson: unknown;
      if (ressourceForm.type === 'EXERCICE') {
        contenuJson = exerciseQuestionsToJson(exerciseQuestions);
      }
      await createAdminElearningRessource({
        idFiliere,
        idCours: ressourceForm.idCours || null,
        type: tab === 'bibliotheque' ? 'BIBLIOTHEQUE' : ressourceForm.type,
        titre: ressourceForm.titre,
        description: ressourceForm.description || undefined,
        urlExterne: ressourceForm.urlExterne || null,
        urlFichier: ressourceForm.urlFichier || null,
        storageKey: ressourceForm.storageKey || null,
        contenuJson,
        estPublie: ressourceForm.estPublie,
        auteurBibliotheque: tab === 'bibliotheque' ? ressourceForm.auteurBibliotheque || null : undefined,
        categorieBibliotheque: tab === 'bibliotheque' ? ressourceForm.categorieBibliotheque || null : undefined,
        tagsBibliotheque:
          tab === 'bibliotheque' && ressourceForm.tagsBibliotheque
            ? ressourceForm.tagsBibliotheque.split(',').map((t) => t.trim()).filter(Boolean)
            : undefined,
        langueBibliotheque: tab === 'bibliotheque' ? ressourceForm.langueBibliotheque || null : undefined,
        anneePublication:
          tab === 'bibliotheque' && ressourceForm.anneePublication
            ? Number(ressourceForm.anneePublication)
            : undefined,
      });
      setRessourceForm({
        type: tab === 'bibliotheque' ? 'BIBLIOTHEQUE' : 'VIDEO',
        idCours: '',
        titre: '',
        description: '',
        urlExterne: '',
        urlFichier: '',
        storageKey: '',
        estPublie: false,
        auteurBibliotheque: '',
        categorieBibliotheque: '',
        tagsBibliotheque: '',
        langueBibliotheque: '',
        anneePublication: '',
      });
      setExerciseQuestions([]);
      showToast('Contenu ajouté', 'success');
      await loadTabData();
      await loadBase();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateForum(e: React.FormEvent) {
    e.preventDefault();
    if (!idFiliere || !forumForm.titre.trim()) return;
    setBusy(true);
    try {
      await createAdminElearningForum({ idFiliere, ...forumForm });
      setForumForm({ titre: '', description: '', estOuvert: true });
      showToast('Forum créé', 'success');
      await loadTabData();
      await loadBase();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  }

  const sideCard = useMemo(
    () => ({
      label: 'Filières',
      value: loading ? '…' : String(filieres.length),
      helper: 'Formations avec contenu en ligne',
      sideLinks: [{ label: 'Formations', to: '/admin/filieres', icon: 'school' }],
    }),
    [filieres.length, loading],
  );

  const tabs: Array<{ id: TabId; label: string; icon: string }> = [
    { id: 'vue', label: 'Vue d\'ensemble', icon: 'layout_dashboard' },
    { id: 'stats', label: 'Suivi apprenants', icon: 'bar_chart' },
    { id: 'cours', label: 'Cours en ligne', icon: 'menu_book' },
    { id: 'ressources', label: 'Vidéos & supports', icon: 'video' },
    { id: 'bibliotheque', label: 'Bibliothèque', icon: 'library_big' },
    { id: 'forums', label: 'Forums', icon: 'forum' },
    { id: 'moderation', label: 'Modération', icon: 'shield' },
  ];

  return (
    <AdminPageShell
      title="E-learning"
      description="Gérez les cours, vidéos, supports, exercices, la bibliothèque et les forums pour vos apprenants."
      sideCard={sideCard}
      isLoading={loading && tab === 'vue'}
      onRetry={() => void loadBase()}
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant} onClose={() => setToast(null)} />

      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`academic-panel-btn ${tab === t.id ? 'academic-panel-btn--primary' : 'academic-panel-btn--secondary'}`}
          >
            <LucideSymbolIcon name={t.icon} tone="inherit" size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'vue' ? (
        <div className="mb-4 max-w-md">
          <label className="mb-1 block text-xs font-bold text-on-surface-variant">Filière</label>
          <AcademiaSelect value={idFiliere} onChange={(e) => setIdFiliere(e.target.value)}>
            {filieres.map((f) => (
              <option key={f.id} value={f.id}>
                {f.code} — {f.libelle}
              </option>
            ))}
          </AcademiaSelect>
        </div>
      ) : null}

      {loading && tab === 'vue' ? (
        <p className="text-sm text-on-surface-variant">Chargement…</p>
      ) : null}

      {tab === 'vue' && dashboard ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <AcademicPanelCard title="Contenus proposés" icon="check_circle">
            <p className="mb-3 text-sm text-on-surface-variant">
              Indique si chaque type de contenu est déjà disponible pour vos apprenants.
            </p>
            <ul className="space-y-2">
              {FICHE_ITEMS.map((item) => (
                <li key={item.key} className="flex items-center justify-between gap-3 text-sm">
                  <span>{item.label}</span>
                  <span
                    className={
                      couverture[item.key]
                        ? 'academic-panel-badge academic-panel-badge--success'
                        : 'academic-panel-badge academic-panel-badge--muted'
                    }
                  >
                    {couverture[item.key] ? 'Disponible' : 'À ajouter'}
                  </span>
                </li>
              ))}
            </ul>
          </AcademicPanelCard>
          <AcademicPanelCard title="Quantités" icon="bar_chart">
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ['Cours', dashboard.coursEnLigne],
                  ['Vidéos', dashboard.videos],
                  ['Supports', dashboard.supports],
                  ['Exercices', dashboard.exercices],
                  ['Bibliothèque', dashboard.bibliotheque],
                  ['Forums', dashboard.forums],
                ] as Array<[string, unknown]>
              ).map(([label, value]) => (
                <div key={String(label)} className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-3 text-center">
                  <p className="text-[10px] font-bold uppercase text-on-surface-variant">{label}</p>
                  <p className="text-2xl font-black text-primary">{String(value ?? 0)}</p>
                </div>
              ))}
            </div>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'cours' ? (
        <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
          <AcademicPanelCard title="Cours publiés" icon="menu_book">
            <AcademicPanelTableWrap>
              <table className="academic-panel-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Contenus</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cours.map((c) => (
                    <tr key={String(c.id)}>
                      <td>{String(c.titre)}</td>
                      <td>{String(c.ressourcesCount ?? 0)}</td>
                      <td>
                        <span className={c.estPublie ? 'academic-panel-badge academic-panel-badge--success' : 'academic-panel-badge academic-panel-badge--muted'}>
                          {c.estPublie ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="academic-panel-btn academic-panel-btn--secondary !px-2 !py-1 text-xs"
                          onClick={async () => {
                            try {
                              await patchAdminElearningCours(String(c.id), { estPublie: !c.estPublie });
                              await loadTabData();
                            } catch (err) {
                              showToast(err instanceof Error ? err.message : 'Erreur', 'error');
                            }
                          }}
                        >
                          {c.estPublie ? 'Dépublier' : 'Publier'}
                        </button>
                        {canDelete ? (
                          <AdminTableDeleteButton
                            canDelete
                            label="Suppr."
                            className="!ml-1 !px-2 !py-1"
                            confirmMessage="Supprimer ce cours ? Cette action est irréversible."
                            onDelete={async () => {
                              await deleteAdminElearningCours(String(c.id));
                              await loadTabData();
                            }}
                          />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
          <AcademicPanelCard title="Nouveau cours" icon="add">
            <form className="space-y-3" onSubmit={handleCreateCours}>
              <input className={fieldClass} placeholder="Titre" value={coursForm.titre} onChange={(e) => setCoursForm({ ...coursForm, titre: e.target.value })} required />
              <textarea className={fieldClass} rows={3} placeholder="Description" value={coursForm.description} onChange={(e) => setCoursForm({ ...coursForm, description: e.target.value })} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={coursForm.estPublie} onChange={(e) => setCoursForm({ ...coursForm, estPublie: e.target.checked })} />
                Publier immédiatement
              </label>
              <button type="submit" disabled={busy} className="academic-panel-btn academic-panel-btn--primary w-full !justify-center">
                Créer
              </button>
            </form>
          </AcademicPanelCard>
        </div>
      ) : null}

      {(tab === 'ressources' || tab === 'bibliotheque') ? (
        <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
          <AcademicPanelCard title={tab === 'bibliotheque' ? 'Bibliothèque numérique' : 'Vidéos, supports et exercices'} icon={tab === 'bibliotheque' ? 'library_big' : 'video'}>
            {tab === 'bibliotheque' ? (
              <div className="mb-4 grid gap-2 md:grid-cols-3">
                <input
                  className={fieldClass}
                  placeholder="Rechercher…"
                  value={biblioFilters.q}
                  onChange={(e) => setBiblioFilters({ ...biblioFilters, q: e.target.value })}
                />
                <AcademiaSelect
                  value={biblioFilters.categorie}
                  onChange={(e) => setBiblioFilters({ ...biblioFilters, categorie: e.target.value })}
                >
                  <option value="">Toutes les catégories</option>
                  {BIBLIOTHEQUE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </AcademiaSelect>
                <input
                  className={fieldClass}
                  placeholder="Mot-clé"
                  value={biblioFilters.tag}
                  onChange={(e) => setBiblioFilters({ ...biblioFilters, tag: e.target.value })}
                />
              </div>
            ) : null}
            <ul className="academic-panel-list">
            {ressources.filter((r) => tab !== 'ressources' || r.type !== 'BIBLIOTHEQUE').map((r) => (
              <AcademicPanelListItem key={String(r.id)}>
                <div className="flex w-full items-start justify-between gap-3">
                  <div>
                    <span className="block font-medium text-slate-900">{String(r.titre)}</span>
                    <span className="text-xs text-on-surface-variant">
                      {labelRessourceType(r.type)}
                      {r.estPublie ? '' : ' · brouillon'}
                      {r.categorieBibliotheque ? ` · ${String(r.categorieBibliotheque)}` : ''}
                      {r.auteurBibliotheque ? ` · ${String(r.auteurBibliotheque)}` : ''}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--secondary !px-2 !py-1 text-xs"
                      onClick={async () => {
                        await patchAdminElearningRessource(String(r.id), { estPublie: !r.estPublie });
                        await loadTabData();
                      }}
                    >
                      {r.estPublie ? 'Dépublier' : 'Publier'}
                    </button>
                    {canDelete ? (
                      <AdminTableDeleteButton
                        canDelete
                        label="Suppr."
                        className="!px-2 !py-1"
                        confirmMessage="Supprimer ce contenu ? Cette action est irréversible."
                        onDelete={async () => {
                          await deleteAdminElearningRessource(String(r.id));
                          await loadTabData();
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </AcademicPanelListItem>
            ))}
            </ul>
            {ressources.length === 0 ? <p className="text-sm text-on-surface-variant">Aucun contenu pour cette filière.</p> : null}
          </AcademicPanelCard>
          <AcademicPanelCard title="Ajouter un contenu" icon="add">
            <form className="space-y-3" onSubmit={handleCreateRessource}>
              {tab === 'ressources' ? (
                <>
                  <AcademiaSelect
                    value={ressourceForm.type}
                    onChange={(e) => setRessourceForm({ ...ressourceForm, type: e.target.value })}
                  >
                    {RESSOURCE_TYPES.filter((t) => t.value !== 'BIBLIOTHEQUE').map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </AcademiaSelect>
                  <AcademiaSelect
                    value={ressourceForm.idCours}
                    onChange={(e) => setRessourceForm({ ...ressourceForm, idCours: e.target.value })}
                  >
                    {coursOptions.map((o) => (
                      <option key={o.value || 'none'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </AcademiaSelect>
                </>
              ) : null}
              <input className={fieldClass} placeholder="Titre" value={ressourceForm.titre} onChange={(e) => setRessourceForm({ ...ressourceForm, titre: e.target.value })} required />
              {tab === 'bibliotheque' ? (
                <>
                  <input className={fieldClass} placeholder="Auteur" value={ressourceForm.auteurBibliotheque} onChange={(e) => setRessourceForm({ ...ressourceForm, auteurBibliotheque: e.target.value })} />
                  <AcademiaSelect
                    value={ressourceForm.categorieBibliotheque}
                    onChange={(e) => setRessourceForm({ ...ressourceForm, categorieBibliotheque: e.target.value })}
                    required
                  >
                    <option value="">Choisir une catégorie</option>
                    {BIBLIOTHEQUE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </AcademiaSelect>
                  <input className={fieldClass} placeholder="Mots-clés (séparés par des virgules)" value={ressourceForm.tagsBibliotheque} onChange={(e) => setRessourceForm({ ...ressourceForm, tagsBibliotheque: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <AcademiaSelect
                      value={ressourceForm.langueBibliotheque}
                      onChange={(e) => setRessourceForm({ ...ressourceForm, langueBibliotheque: e.target.value })}
                      required
                    >
                      <option value="">Choisir une langue</option>
                      {BIBLIOTHEQUE_LANGUES.map((l) => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </AcademiaSelect>
                    <input className={fieldClass} type="number" placeholder="Année" value={ressourceForm.anneePublication} onChange={(e) => setRessourceForm({ ...ressourceForm, anneePublication: e.target.value })} />
                  </div>
                </>
              ) : null}
              <ElearningFileUpload
                onUploaded={({ storageKey }) => setRessourceForm({ ...ressourceForm, storageKey })}
                label="Téléverser vidéo / PDF / image"
              />
              {ressourceForm.storageKey ? (
                <p className="text-xs text-emerald-700">Fichier téléversé — prêt à publier</p>
              ) : null}
              <input className={fieldClass} placeholder="Lien vidéo (YouTube, Vimeo…)" value={ressourceForm.urlExterne} onChange={(e) => setRessourceForm({ ...ressourceForm, urlExterne: e.target.value })} />
              <input className={fieldClass} placeholder="Lien document en ligne (optionnel)" value={ressourceForm.urlFichier} onChange={(e) => setRessourceForm({ ...ressourceForm, urlFichier: e.target.value })} />
              {ressourceForm.type === 'EXERCICE' ? (
                <ElearningExerciseEditor value={exerciseQuestions} onChange={setExerciseQuestions} />
              ) : null}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={ressourceForm.estPublie} onChange={(e) => setRessourceForm({ ...ressourceForm, estPublie: e.target.checked })} />
                Publier
              </label>
              <button type="submit" disabled={busy} className="academic-panel-btn academic-panel-btn--primary w-full !justify-center">
                Ajouter
              </button>
            </form>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'forums' ? (
        <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
          <AcademicPanelCard title="Forums pédagogiques" icon="forum">
            <ul className="academic-panel-list">
            {forums.map((f) => (
              <AcademicPanelListItem key={String(f.id)}>
                <div className="flex w-full items-start justify-between gap-3">
                  <div>
                    <span className="block font-medium text-slate-900">{String(f.titre)}</span>
                    <span className="text-xs text-on-surface-variant">{String(f.messagesCount ?? 0)} message(s)</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--secondary !px-2 !py-1 text-xs"
                      onClick={async () => {
                        await patchAdminElearningForum(String(f.id), { estOuvert: !f.estOuvert });
                        await loadTabData();
                      }}
                    >
                      {f.estOuvert ? 'Fermer' : 'Ouvrir'}
                    </button>
                    {canDelete ? (
                      <AdminTableDeleteButton
                        canDelete
                        label="Suppr."
                        className="!px-2 !py-1"
                        confirmMessage="Supprimer ce forum ? Cette action est irréversible."
                        onDelete={async () => {
                          await deleteAdminElearningForum(String(f.id));
                          await loadTabData();
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </AcademicPanelListItem>
            ))}
            </ul>
          </AcademicPanelCard>
          <AcademicPanelCard title="Nouveau forum" icon="add">
            <form className="space-y-3" onSubmit={handleCreateForum}>
              <input className={fieldClass} placeholder="Titre" value={forumForm.titre} onChange={(e) => setForumForm({ ...forumForm, titre: e.target.value })} required />
              <textarea className={fieldClass} rows={3} placeholder="Description" value={forumForm.description} onChange={(e) => setForumForm({ ...forumForm, description: e.target.value })} />
              <button type="submit" disabled={busy} className="academic-panel-btn academic-panel-btn--primary w-full !justify-center">
                Créer le forum
              </button>
            </form>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'stats' && stats ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm text-slate-800">
            <p className="font-semibold text-primary">À quoi sert cet onglet ?</p>
            <p className="mt-1">
              Vous voyez ici <strong>combien d&apos;apprenants ont terminé chaque contenu</strong> (vidéo, support,
              exercice, document de bibliothèque…).
            </p>
            <p className="mt-2 text-on-surface-variant">
              Un contenu compte comme terminé lorsque l&apos;apprenant clique sur «&nbsp;Marquer comme terminé&nbsp;»
              ou lorsqu&apos;un exercice est validé (correction automatique ou par le formateur).
            </p>
          </div>
          <AcademicPanelCard title="Vue d'ensemble" icon="bar_chart">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {(
                [
                  ['Apprenants inscrits', (stats.synthese as Record<string, unknown>)?.apprenantsEligibles],
                  ['Contenus publiés', (stats.synthese as Record<string, unknown>)?.ressourcesPubliees],
                  ['Contenus terminés', (stats.synthese as Record<string, unknown>)?.progressionsTerminees],
                  ['Taux de participation', `${(stats.synthese as Record<string, unknown>)?.tauxGlobalPct ?? 0} %`],
                ] as Array<[string, unknown]>
              ).map(([label, value]) => (
                <div key={String(label)} className="rounded-xl border border-outline-variant/20 p-3 text-center">
                  <p className="text-[10px] font-bold uppercase text-on-surface-variant">{label}</p>
                  <p className="text-2xl font-black text-primary">{String(value ?? 0)}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-on-surface-variant">
              Le taux de participation indique, en pourcentage, la part des contenus terminés par rapport au nombre
              possible (apprenants inscrits × contenus publiés).
            </p>
          </AcademicPanelCard>
          <AcademicPanelCard title="Détail par contenu" icon="list">
            <AcademicPanelTableWrap>
              <table className="academic-panel-table">
                <thead>
                  <tr>
                    <th>Contenu</th>
                    <th>Type</th>
                    <th>Apprenants ayant terminé</th>
                    <th>Taux</th>
                    <th>Note moyenne</th>
                  </tr>
                </thead>
                <tbody>
                  {((stats.parRessource as Array<Record<string, unknown>>) ?? []).map((r) => (
                    <tr key={String(r.id)}>
                      <td>{String(r.titre)}</td>
                      <td>{labelRessourceType(r.type)}</td>
                      <td>
                        {String(r.completions)} sur {String(r.apprenantsEligibles)}
                      </td>
                      <td>{String(r.tauxCompletionPct)} %</td>
                      <td>{r.scoreMoyen != null ? `${r.scoreMoyen} %` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'moderation' ? (
        <div className="grid gap-4 xl:grid-cols-[280px_1fr]">
          <AcademicPanelCard title="Forums" icon="forum">
            {forums.map((f) => (
              <button
                key={String(f.id)}
                type="button"
                className={`mb-2 w-full rounded-lg border px-3 py-2 text-left text-sm ${selectedForumId === f.id ? 'border-primary bg-primary/5' : 'border-outline-variant/20'}`}
                onClick={async () => {
                  setSelectedForumId(String(f.id));
                  setForumMessages(await fetchAdminElearningForumMessages(String(f.id)));
                }}
              >
                {String(f.titre)}
              </button>
            ))}
          </AcademicPanelCard>
          <AcademicPanelCard title="Messages — modération" icon="shield">
            <input
              className={`${fieldClass} mb-3`}
              placeholder="Motif de modération (optionnel)"
              value={moderationMotif}
              onChange={(e) => setModerationMotif(e.target.value)}
            />
            {forumMessages.map((m) => (
              <div key={String(m.id)} className="mb-3 rounded-lg border border-outline-variant/20 p-3 text-sm">
                <p className="text-xs font-bold text-primary">
                  {String(m.auteur)} · {FORUM_MESSAGE_STATUT_LABELS[String(m.statut)] ?? String(m.statut)}
                </p>
                <p className="mt-1">{String(m.contenu)}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="academic-panel-btn academic-panel-btn--secondary !px-2 !py-1 text-xs"
                    onClick={async () => {
                      await moderateAdminElearningForumMessage(String(m.id), { statut: 'MASQUE', motif: moderationMotif });
                      setForumMessages(await fetchAdminElearningForumMessages(selectedForumId));
                      showToast('Message masqué — notification envoyée', 'success');
                    }}
                  >
                    Masquer
                  </button>
                  <button
                    type="button"
                    className="academic-panel-btn academic-panel-btn--danger !px-2 !py-1 text-xs"
                    onClick={async () => {
                      await moderateAdminElearningForumMessage(String(m.id), { statut: 'SUPPRIME', motif: moderationMotif });
                      setForumMessages(await fetchAdminElearningForumMessages(selectedForumId));
                      showToast('Message supprimé', 'success');
                    }}
                  >
                    Supprimer
                  </button>
                  {m.statut !== 'VISIBLE' ? (
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--secondary !px-2 !py-1 text-xs"
                      onClick={async () => {
                        await moderateAdminElearningForumMessage(String(m.id), { statut: 'VISIBLE' });
                        setForumMessages(await fetchAdminElearningForumMessages(selectedForumId));
                      }}
                    >
                      Restaurer
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {!selectedForumId ? (
              <p className="text-sm text-on-surface-variant">Sélectionnez un forum pour modérer les messages.</p>
            ) : null}
          </AcademicPanelCard>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
